import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, orderBy, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from './firebase';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import 'jspdf-autotable';
import './AdminDashboard.css';
import HOMonthEndView from './HOMonthEndView';
import HOAllFranchisees from './HOAllFranchisees';
import HOAllStudents from './HOAllStudents';
import HOFranchiseeCharges from './HOFranchiseeCharges';
import AdminFeeStructureSetup from './AdminFeeStructureSetup';
import AdminRecalculateStudentBalances from './AdminRecalculateStudentBalances';
import HOTshirtOrders from './HOTshirtOrders';
import RepairTshirtOrder from './RepairTshirtOrder';
import HOTshirtInvoiceReport from './HOTshirtInvoiceReport';
import HOTshirtRecon from './HOTshirtRecon';
import AdminHandoutTracker from './AdminHandoutTracker';
import AdminImpersonate from './AdminImpersonate';
import HOFranchiseeQBSetup from './HOFranchiseeQBSetup';
import HOInvoiceExport from './HOInvoiceExport';



function AdminDashboard() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [orders, setOrders] = useState([]);
  const [franchisees, setFranchisees] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dashboard stats
  const [stats, setStats] = useState({
    totalFranchisees: 0,
    totalSchools: 0,
    totalStudents: 0,
    pendingSubmissions: 0
  });

  useEffect(() => {
    loadData();
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      // Count franchisees
      const franchiseesSnapshot = await getDocs(collection(db, 'Franchisees'));
      const totalFranchisees = franchiseesSnapshot.size;

      // Count schools
      const schoolsSnapshot = await getDocs(collection(db, 'schools'));
      const totalSchools = schoolsSnapshot.size;

      // Count active students
      const studentsQuery = query(
        collection(db, 'students'),
        where('isActive', '==', true)
      );
      const studentsSnapshot = await getDocs(studentsQuery);
      const totalStudents = studentsSnapshot.size;

      // Count pending month-end submissions for current month
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const submissionsQuery = query(
        collection(db, 'monthEndSubmissions'),
        where('month', '==', currentMonth),
        where('year', '==', currentYear)
      );
      const submissionsSnapshot = await getDocs(submissionsQuery);
      const submittedCount = submissionsSnapshot.size;
      const pendingSubmissions = totalFranchisees - submittedCount;

      setStats({
        totalFranchisees,
        totalSchools,
        totalStudents,
        pendingSubmissions: Math.max(0, pendingSubmissions)
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const loadData = async () => {
    try {
      // Load all franchisees first
      const franchiseesSnapshot = await getDocs(collection(db, 'Franchisees'));
      const franchiseesMap = {};
      franchiseesSnapshot.docs.forEach(doc => {
        franchiseesMap[doc.id] = { id: doc.id, ...doc.data() };
      });
      setFranchisees(franchiseesMap);

      // Load all orders
      const q = query(collection(db, 'marketingOrders'), orderBy('orderDate', 'desc'));
      const ordersSnapshot = await getDocs(q);
      const ordersList = await Promise.all(ordersSnapshot.docs.map(async (docSnapshot) => {
        const orderData = docSnapshot.data();
        
        // Get franchisee ID from reference
        let franchiseeId = null;
        if (orderData.franchiseeRef) {
          if (typeof orderData.franchiseeRef === 'string') {
            franchiseeId = orderData.franchiseeRef.split('/').pop();
          } else if (orderData.franchiseeRef.id) {
            franchiseeId = orderData.franchiseeRef.id;
          } else if (orderData.franchiseeRef.path) {
            franchiseeId = orderData.franchiseeRef.path.split('/').pop();
          } else if (orderData.franchiseeRef._key) {
            franchiseeId = orderData.franchiseeRef._key.path.segments[orderData.franchiseeRef._key.path.segments.length - 1];
          }
        }
        
        return {
          id: docSnapshot.id,
          ...orderData,
          franchiseeId: franchiseeId
        };
      }));
      
      setOrders(ordersList);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'marketingOrders', orderId), {
        status: newStatus
      });
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      
      alert(`Order status changed to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error changing order status');
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const { id, franchiseeRef, orderDate, year, ...orderData } = editingOrder;
      
      await updateDoc(doc(db, 'marketingOrders', id), orderData);
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === id ? editingOrder : order
      ));
      
      setEditingOrder(null);
      alert('Order updated successfully!');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Error updating order');
    }
  };

  const handleCancelEdit = () => {
    setEditingOrder(null);
  };

  const generatePDF = (order) => {
    const franchiseeId = order.franchiseeId;
    const franchisee = franchisees[franchiseeId];
    
    // Use name from order form if franchisee lookup fails
    const displayName = franchisee?.name || order.feeSlipName || 'Unknown';
    const displayEmail = franchisee?.email || order.feeSlipEmail || '';
    
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(65, 105, 225);
    doc.text('Marketing Material Order', 105, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Order Date: ${formatDate(order.orderDate)}`, 15, 45);
    doc.text(`Franchisee: ${displayName}`, 15, 50);
    doc.text(`Email: ${displayEmail}`, 15, 55);
    doc.text(`Status: ${order.status}`, 15, 60);
    
    let yPos = 70;
    
    // Section A
    if (order.colourBrochuresQty) {
      doc.setFontSize(14);
      doc.text('A) Colour Brochures', 15, yPos);
      yPos += 7;
      doc.setFontSize(10);
      doc.text(`Quantity: ${order.colourBrochuresQty}`, 20, yPos);
      yPos += 10;
    }
    
    // Section B
    if (order.feeSlipName || order.feeSlipEmail) {
      doc.setFontSize(14);
      doc.text('B) Fee and Contact Slips Information', 15, yPos);
      yPos += 7;
      doc.setFontSize(10);
      if (order.feeSlipName) {
        doc.text(`Name: ${order.feeSlipName}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipLandline) {
        doc.text(`Landline: ${order.feeSlipLandline}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipCellphone) {
        doc.text(`Cellphone: ${order.feeSlipCellphone}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipEmail) {
        doc.text(`Email: ${order.feeSlipEmail}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipBank) {
        doc.text(`Bank: ${order.feeSlipBank}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipBeneficiary) {
        doc.text(`Beneficiary: ${order.feeSlipBeneficiary}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipAccountNumber) {
        doc.text(`Account Number: ${order.feeSlipAccountNumber}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipBranchCode) {
        doc.text(`Branch Code: ${order.feeSlipBranchCode}`, 20, yPos);
        yPos += 5;
      }
      if (order.feeSlipReference) {
        doc.text(`Reference: ${order.feeSlipReference}`, 20, yPos);
        yPos += 5;
      }
      yPos += 5;
    }
    
    // Section C - Fee Structures
    doc.setFontSize(14);
    doc.text('C) Fee and Contact Slip Quantities', 15, yPos);
    yPos += 7;
    doc.setFontSize(10);
    
    if (order.c1Qty) {
      doc.text('4 Term Schools:', 20, yPos);
      yPos += 5;
      doc.text(`  Term Fee: R${order.c1TermFee}, Instalment: R${order.c1InstalmentFee}, Qty: ${order.c1Qty}`, 20, yPos);
      if (order.c1Extra) {
        yPos += 5;
        doc.text(`  Extra: ${order.c1Extra}`, 20, yPos);
      }
      yPos += 7;
    }
    
    if (order.c2Qty) {
      doc.text('3 Term Schools:', 20, yPos);
      yPos += 5;
      doc.text(`  Term Fee: R${order.c2TermFee}, Instalment: R${order.c2InstalmentFee}, Qty: ${order.c2Qty}`, 20, yPos);
      if (order.c2Extra) {
        yPos += 5;
        doc.text(`  Extra: ${order.c2Extra}`, 20, yPos);
      }
      yPos += 7;
    }
    
    if (order.c3Qty) {
      doc.text('Other Fee Structures:', 20, yPos);
      yPos += 5;
      doc.text(`  Term Fee: R${order.c3TermFee}, Instalment: R${order.c3InstalmentFee}, Qty: ${order.c3Qty}`, 20, yPos);
      if (order.c3Extra) {
        yPos += 5;
        doc.text(`  Extra: ${order.c3Extra}`, 20, yPos);
      }
      yPos += 7;
    }
    
    // Section D - Other Materials
    if (order.complimentSlipsQty || order.marketingLettersQty || order.postersQty) {
      yPos += 3;
      doc.setFontSize(14);
      doc.text('D) Other Materials', 15, yPos);
      yPos += 7;
      doc.setFontSize(10);
      if (order.complimentSlipsQty) {
        doc.text(`Compliment Slips: ${order.complimentSlipsQty}`, 20, yPos);
        yPos += 5;
      }
      if (order.marketingLettersQty) {
        doc.text(`Marketing Letters: ${order.marketingLettersQty}`, 20, yPos);
        yPos += 5;
      }
      if (order.postersQty) {
        doc.text(`Posters: ${order.postersQty}`, 20, yPos);
        yPos += 5;
      }
    }
    
    // Delivery
    yPos += 5;
    doc.setFontSize(14);
    doc.text('Delivery Information', 15, yPos);
    yPos += 7;
    doc.setFontSize(10);
    doc.text(`Method: ${order.deliveryMethod === 'courier' ? 'Courier' : 'Collection in Randburg'}`, 20, yPos);
    yPos += 5;
    
    if (order.deliveryMethod === 'courier' && order.deliveryStreet) {
      doc.text(`Address: ${order.deliveryStreet}`, 20, yPos);
      yPos += 5;
      if (order.deliveryStreet2) {
        doc.text(`${order.deliveryStreet2}`, 20, yPos);
        yPos += 5;
      }
      doc.text(`${order.deliveryCity}, ${order.deliveryProvince} ${order.deliveryPostalCode}`, 20, yPos);
    }
    
    // Save
    doc.save(`BABS_Order_${displayName}_${formatDate(order.orderDate)}.pdf`);
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const exportToExcel = () => {
    // Create data array matching Jotform format
    const excelData = [
      // Header row
      [
        'Submission Date',
        'Name and Surname',
        'Quantity colour brochures:',
        'Name and Surname:',
        'Landline:',
        'Cell phone no.:',
        'E-mail address:',
        'Bank:',
        'Beneficiary name:',
        'Account number:',
        'Branch code:',
        'Reference parents must type:',
        'C1)Term Fee :',
        'C1) Instalment fee:',
        'C1) Qty to order:',
        'C1) Extra you want to add:',
        'C2) Term Fee',
        'C2) Instalment Fee:',
        'C2) Qty to order:',
        'C2) Extra you want to add',
        'C3) Term Fee:',
        'C3 Instalment Fee:',
        'C3) Quantity to order',
        'C4) Extra you want to add',
        'Compliment slips Qty:',
        'Marketing Letters Qty:',
        'Posters Qty:',
        'Please select one:',
        'Street Address',
        'Street Address Line 2',
        'City',
        'State / Province',
        'Postal / Zip Code',
        'Status'
      ]
    ];

    // Add data rows
    orders.forEach(order => {
      const franchiseeId = order.franchiseeId;
      const franchisee = franchisees[franchiseeId];
      const displayName = franchisee?.name || order.feeSlipName || '';
      
      excelData.push([
        formatDate(order.orderDate),
        displayName,
        order.colourBrochuresQty || '',
        order.feeSlipName || '',
        order.feeSlipLandline || '',
        order.feeSlipCellphone || '',
        order.feeSlipEmail || '',
        order.feeSlipBank || '',
        order.feeSlipBeneficiary || '',
        order.feeSlipAccountNumber || '',
        order.feeSlipBranchCode || '',
        order.feeSlipReference || '',
        order.c1TermFee || '',
        order.c1InstalmentFee || '',
        order.c1Qty || '',
        order.c1Extra || '',
        order.c2TermFee || '',
        order.c2InstalmentFee || '',
        order.c2Qty || '',
        order.c2Extra || '',
        order.c3TermFee || '',
        order.c3InstalmentFee || '',
        order.c3Qty || '',
        order.c3Extra || '',
        order.complimentSlipsQty || '',
        order.marketingLettersQty || '',
        order.postersQty || '',
        order.deliveryMethod === 'courier' ? 'Please courier to me' : 'I will collect my order in Randburg, Gauteng',
        order.deliveryStreet || '',
        order.deliveryStreet2 || '',
        order.deliveryCity || '',
        order.deliveryProvince || '',
        order.deliveryPostalCode || '',
        order.status || ''
      ]);
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');

    // Generate filename with current date
    const filename = `Marketing_Orders_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-ZA');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#FFA500';
      case 'processing': return '#4169E1';
      case 'completed': return '#28a745';
      default: return '#666';
    }
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === filterStatus);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  // Month-End View
  if (currentView === 'monthEnd') {
    return (
      <HOMonthEndView onClose={() => setCurrentView('dashboard')} />
    );
  }

  // All Franchisees View
  if (currentView === 'franchisees') {
    return (
      <HOAllFranchisees onClose={() => setCurrentView('dashboard')} />
    );
  }

  // All Students View
  if (currentView === 'students') {
    return (
      <HOAllStudents onClose={() => setCurrentView('dashboard')} />
    );
  }

  // Franchisee Charges View
  if (currentView === 'franchiseeCharges') {
    return (
      <HOFranchiseeCharges onClose={() => setCurrentView('dashboard')} />
    );
  }
  // Recalculate Student Balances View
  if (currentView === 'recalculateBalances') {
    return (
      <AdminRecalculateStudentBalances onClose={() => setCurrentView('dashboard')} />
    );
  }

  if (currentView === 'tshirtOrders') {
    return (
      <HOTshirtOrders onClose={() => setCurrentView('dashboard')} />
    );
  }

  // T-Shirt Invoice Report View
  if (currentView === 'tshirtInvoiceReport') {
    return (
      <HOTshirtInvoiceReport onClose={() => setCurrentView('dashboard')} />
    );
  }
  // T-Shirt Reconciliation View
  if (currentView === 'tshirtRecon') {
    return (
      <div>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a6e 0%, #2B4BA0 100%)',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => setCurrentView('dashboard')}
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.4)',
              padding: '8px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
        <HOTshirtRecon onClose={() => setCurrentView('dashboard')} />
      </div>
    );
  }
  // TEMPORARY REPAIR TOOL - REMOVE AFTER USE
  if (currentView === 'repairOrder') {
    return (
      <RepairTshirtOrder onClose={() => setCurrentView('dashboard')} />
    );
  }
  if (currentView === 'handoutTracker') {
  return <AdminHandoutTracker onClose={() => setCurrentView('dashboard')} />;
}
if (currentView === 'impersonate') {
    return <AdminImpersonate onClose={() => setCurrentView('dashboard')} />;
  }
  // QuickBooks Setup View
  if (currentView === 'qbSetup') {
    return <HOFranchiseeQBSetup onClose={() => setCurrentView('dashboard')} />;
  }
  // Invoice Export View
  if (currentView === 'invoiceExport') {
    return <HOInvoiceExport onClose={() => setCurrentView('dashboard')} />;
  }
  // Admin Fee Setup View
  if (currentView === 'adminFeeSetup') {
    return (
      <div className="admin-fee-setup-container">
        <div className="setup-nav-bar">
          <button onClick={() => setCurrentView('dashboard')} className="back-btn">
            â† Back to Dashboard
          </button>
        </div>
        <AdminFeeStructureSetup onClose={() => setCurrentView('dashboard')} />
      </div>
    );
  }

  // Marketing Orders View
  if (currentView === 'marketingOrders') {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <div>
            <h1>ðŸ“¦ Marketing Orders</h1>
            <p>Logged in as: {auth.currentUser?.email}</p>
          </div>
          <div className="header-buttons">
            <button onClick={() => setCurrentView('dashboard')} className="back-btn">
              â† Back to Dashboard
            </button>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>

        <div className="dashboard-content">
          <div className="filters">
            <label>Filter by Status:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Orders</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
            </select>
            <span className="order-count">{filteredOrders.length} orders</span>
            <button onClick={exportToExcel} className="export-excel-btn">
              Export All to Excel
            </button>
          </div>

          {/* Edit Modal */}
          {editingOrder && (
            <div className="edit-modal-overlay">
              <div className="edit-modal">
                <h2>Edit Order</h2>
                <form onSubmit={handleSaveEdit} className="edit-form">
                  <div className="form-section">
                    <h3>A) Colour Brochures</h3>
                    <input
                      type="number"
                      value={editingOrder.colourBrochuresQty || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, colourBrochuresQty: e.target.value})}
                      placeholder="Quantity"
                    />
                  </div>

                  <div className="form-section">
                    <h3>B) Fee Slip Information</h3>
                    <input
                      type="text"
                      value={editingOrder.feeSlipName || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, feeSlipName: e.target.value})}
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      value={editingOrder.feeSlipEmail || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, feeSlipEmail: e.target.value})}
                      placeholder="Email"
                    />
                    <input
                      type="text"
                      value={editingOrder.feeSlipCellphone || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, feeSlipCellphone: e.target.value})}
                      placeholder="Cellphone"
                    />
                  </div>

                  <div className="form-section">
                    <h3>C1) 4 Term Schools</h3>
                    <input
                      type="number"
                      value={editingOrder.c1Qty || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, c1Qty: e.target.value})}
                      placeholder="Quantity"
                    />
                  </div>

                  <div className="form-section">
                    <h3>C2) 3 Term Schools</h3>
                    <input
                      type="number"
                      value={editingOrder.c2Qty || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, c2Qty: e.target.value})}
                      placeholder="Quantity"
                    />
                  </div>

                  <div className="form-section">
                    <h3>D) Other Materials</h3>
                    <input
                      type="number"
                      value={editingOrder.complimentSlipsQty || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, complimentSlipsQty: e.target.value})}
                      placeholder="Compliment Slips"
                    />
                    <input
                      type="number"
                      value={editingOrder.marketingLettersQty || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, marketingLettersQty: e.target.value})}
                      placeholder="Marketing Letters"
                    />
                    <input
                      type="number"
                      value={editingOrder.postersQty || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, postersQty: e.target.value})}
                      placeholder="Posters"
                    />
                  </div>

                  <div className="modal-buttons">
                    <button type="submit" className="save-button">Save Changes</button>
                    <button type="button" onClick={handleCancelEdit} className="cancel-button">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {filteredOrders.length === 0 ? (
            <div className="no-orders">No orders found</div>
          ) : (
            <div className="orders-table">
              {filteredOrders.map(order => {
                const franchiseeId = order.franchiseeId;
                const franchisee = franchisees[franchiseeId];
                
                const displayName = franchisee?.name || order.feeSlipName || 'Unknown Franchisee';
                const displayEmail = franchisee?.email || order.feeSlipEmail || 'No email';
                
                return (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <div>
                        <h3>{displayName}</h3>
                        <p className="franchisee-email">{displayEmail}</p>
                        <p className="order-date">Order Date: {formatDate(order.orderDate)}</p>
                      </div>
                      <div className="order-status-section">
                        <span 
                          className="status-badge" 
                          style={{ backgroundColor: getStatusColor(order.status) }}
                        >
                          {order.status}
                        </span>
                      </div>
                    </div>

                    <div className="order-details">
                      <div className="detail-row">
                        {order.colourBrochuresQty && <span>Brochures: {order.colourBrochuresQty}</span>}
                        {order.c1Qty && <span>4-Term Slips: {order.c1Qty}</span>}
                        {order.c2Qty && <span>3-Term Slips: {order.c2Qty}</span>}
                        {order.c3Qty && <span>Other Slips: {order.c3Qty}</span>}
                      </div>
                      <div className="detail-row">
                        {order.complimentSlipsQty && <span>Compliment Slips: {order.complimentSlipsQty}</span>}
                        {order.marketingLettersQty && <span>Letters: {order.marketingLettersQty}</span>}
                        {order.postersQty && <span>Posters: {order.postersQty}</span>}
                      </div>
                      <div className="detail-row">
                        <span>Delivery: {order.deliveryMethod === 'courier' ? 'Courier' : 'Collection'}</span>
                      </div>
                    </div>

                    <div className="order-actions">
                      <button onClick={() => handleEditOrder(order)} className="edit-btn">
                        Edit Order
                      </button>
                      <button onClick={() => generatePDF(order)} className="download-btn">
                        Download PDF
                      </button>
                      {order.status === 'pending' && (
                        <button 
                          onClick={() => handleStatusChange(order.id, 'processing')}
                          className="status-btn processing-btn"
                        >
                          Mark Processing
                        </button>
                      )}
                      {order.status === 'processing' && (
                        <button 
                          onClick={() => handleStatusChange(order.id, 'completed')}
                          className="status-btn completed-btn"
                        >
                          Mark Completed
                        </button>
                      )}
                      {order.status === 'completed' && (
                        <button 
                          onClick={() => handleStatusChange(order.id, 'pending')}
                          className="status-btn pending-btn"
                        >
                          Reset to Pending
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Dashboard View
  return (
    <div className="admin-dashboard ho-dashboard">
      <div className="admin-header">
        <div className="header-left">
          <img src="/arty.png" alt="BABS Logo" className="appbar-logo" />
          <div>
            <h1>🏢 BABS Head Office</h1>
            <p>Logged in as: {auth.currentUser?.email}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="logout-button">Logout</button>
      </div>

      <div className="ho-dashboard-content">
        {/* Stats Cards */}
        <div className="ho-stats-container">
          <div className="ho-stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-label">Franchisees</div>
              <div className="stat-value">{stats.totalFranchisees}</div>
            </div>
          </div>

          <div className="ho-stat-card">
            <div className="stat-icon">🏫«</div>
            <div className="stat-info">
              <div className="stat-label">Total Schools</div>
              <div className="stat-value">{stats.totalSchools}</div>
            </div>
          </div>

          <div className="ho-stat-card">
            <div className="stat-icon">🎓</div>
            <div className="stat-info">
              <div className="stat-label">Active Students</div>
              <div className="stat-value">{stats.totalStudents}</div>
            </div>
          </div>

          <div className="ho-stat-card pending">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <div className="stat-label">Pending Month-Ends</div>
              <div className="stat-value">{stats.pendingSubmissions}</div>
            </div>
          </div>
        </div>

        {/* Category Grid */}
        <div className="ho-category-grid">
          <button 
            className="ho-category-card card-blue"
            onClick={() => setCurrentView('monthEnd')}
          >
            <span className="category-icon">📊</span>
            <span className="category-title">Month-End</span>
            <span className="category-subtitle">Submissions</span>
          </button>

          <button
            className="ho-category-card card-orange"
            onClick={() => setCurrentView('invoiceExport')}
          >
            <span className="category-icon">📤</span>
            <span className="category-title">Invoice Export</span>
            <span className="category-subtitle">For QuickBooks</span>
          </button>

          <button 
            className="ho-category-card card-green"
            onClick={() => setCurrentView('franchisees')}
          >
            <span className="category-icon">👥</span>
            <span className="category-title">All Franchisees</span>
            <span className="category-subtitle">& Schools</span>
          </button>

          <button 
            className="ho-category-card card-teal"
            onClick={() => setCurrentView('students')}
          >
            <span className="category-icon">🎓</span>
            <span className="category-title">All Students</span>
            <span className="category-subtitle">Payments & Contacts</span>
          </button>

          <button 
            className="ho-category-card card-purple"
            onClick={() => setCurrentView('adminFeeSetup')}
          >
            <span className="category-icon">⚙️</span>
            <span className="category-title">Admin Fee</span>
            <span className="category-subtitle">Configuration</span>
          </button>

          <button
            className="ho-category-card card-blue"
            onClick={() => setCurrentView('qbSetup')}
          >
            <span className="category-icon">🧾</span>
            <span className="category-title">QuickBooks</span>
            <span className="category-subtitle">Customer Setup</span>
          </button>

          <button 
            className="ho-category-card card-yellow"
            onClick={() => setCurrentView('franchiseeCharges')}
          >
            <span className="category-icon">💰</span>
            <span className="category-title">Franchisee</span>
            <span className="category-subtitle">Charges</span>
          </button>

          <button 
            className="ho-category-card card-orange"
            onClick={() => setCurrentView('marketingOrders')}
          >
            <span className="category-icon">📦</span>
            <span className="category-title">Marketing</span>
            <span className="category-subtitle">Orders</span>
          </button>

          <button 
            className="ho-category-card card-red"
            onClick={() => setCurrentView('recalculateBalances')}
          >
            <span className="category-icon">🔧</span>
            <span className="category-title">Fix Student</span>
            <span className="category-subtitle">Balances</span>
          </button>

          <button 
            className="ho-category-card card-pink"
            onClick={() => setCurrentView('tshirtOrders')}
          >
            <span className="category-icon">👕</span>
            <span className="category-title">T-Shirt</span>
            <span className="category-subtitle">Orders</span>
          </button>

          <button 
            className="ho-category-card card-indigo"
            onClick={() => setCurrentView('tshirtInvoiceReport')}
          >
            <span className="category-icon">📋</span>
            <span className="category-title">T-Shirt</span>
            <span className="category-subtitle">Invoice Report</span>
          </button>

          <button 
            className="ho-category-card card-teal"
            onClick={() => setCurrentView('tshirtRecon')}
          >
            <span className="category-icon">🔍</span>
            <span className="category-title">T-Shirt</span>
            <span className="category-subtitle">Reconciliation</span>
          </button>
          <button
  className="ho-category-card card-green"
  onClick={() => setCurrentView('handoutTracker')}
>
  <span className="category-icon">👕</span>
  <span className="category-title">T-Shirt</span>
  <span className="category-subtitle">Handout Tracker</span>
</button>

          {/* TEMPORARY - REMOVE AFTER REPAIR */}
          <button 
            className="ho-category-card card-red"
            onClick={() => setCurrentView('repairOrder')}
          >
            <span className="category-icon">🔧</span>
            <span className="category-title">Repair</span>
            <span className="category-subtitle">Missing Order</span>
          </button>
            <button
            className="ho-category-card card-red"
            onClick={() => setCurrentView('impersonate')}
          >
            <span className="category-icon">🔑</span>
            <span className="category-title">Login as</span>
            <span className="category-subtitle">Franchisee</span>
          </button>
        </div>

        {/* Quick Actions */}
        <div className="ho-quick-actions">
          <h3>📋 Quick Overview</h3>
          <div className="quick-info">
            <p>
              <strong>{new Date().toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}:</strong>{' '}
              {stats.totalFranchisees - stats.pendingSubmissions} of {stats.totalFranchisees} franchisees have submitted month-ends
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;